import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  Label,
  FormGroup,
  Alert,
  UncontrolledTooltip,
} from "reactstrap";
import { getUsers, createUser, updateUser, resetUserPassword } from "./service";

const UserManagementModal = ({ isOpen, toggle }) => {
  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const [addingUser, setAddingUser] = useState(false);
  const [userAddedNotice, setUserAddedNotice] = useState(null);
  const [passwordNotice, setPasswordNotice] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    mobile: "",
    manage_users: false,
    view_price: false,
    receive_order_updates: false,
  });
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (isOpen) {
      getUsers()
        .then((res) => setUsers(res))
        .catch(console.error);
    }
  }, [isOpen]);

  const handleEditToggle = (user) => {
    setEditingId(user.id);
    setEditedUser({ ...user });
    setErrors([]);
  };

  const handleEditChange = (e) => {
    const { name, type, checked, value } = e.target;
    setEditedUser({
      ...editedUser,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleEditSave = () => {
    updateUser(editedUser.id, editedUser)
      .then((res) => {
        setUsers(users.map((u) => (u.id === res.id ? res : u)));
        setEditingId(null);
        setErrors([]);
      })
      .catch((response) => {
        if (response.status === 400) {
          setErrors(response.error);
        } else {
          setErrors(["Sorry, something went wrong"]);
        }
      });
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setErrors([]);
  };

  const handleAddChange = (e) => {
    const { name, type, checked, value } = e.target;
    setNewUser({
      ...newUser,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAddUser = () => {
    createUser(newUser)
      .then((res) => {
        const { user, password } = res;
        setUsers([...users, user]);
        setUserAddedNotice({
          user_name: user.name,
          password: password,
        });
        setAddingUser(false);
        setNewUser({
          name: "",
          mobile: "",
          manage_users: false,
          view_price: false,
          receive_order_updates: false,
        });
        setErrors([]);
      })
      .catch((response) => {
        if (response.status === 400) {
          setErrors(response.error);
        } else {
          setErrors(["Sorry, something went wrong"]);
        }
      });
  };

  const handleResetPassword = (user) => {
    if (!window.confirm(`Reset password for ${user.name}?`)) return;

    resetUserPassword(user.id)
      .then((res) => {
        setPasswordNotice({
          user_name: user.name,
          password: res.password,
        });
      })
      .catch((response) => {
        if (response.error) {
          setErrors(response.error);
        }
      });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Manage Users</ModalHeader>
      <ModalBody>
        {errors.length > 0 && (
          <Alert color="danger" toggle={() => setErrors([])} isOpen>
            <ul className="mb-0">
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}
        {userAddedNotice && (
          <Alert color="success" toggle={() => setUserAddedNotice(null)} isOpen>
            <div>
              <b>{userAddedNotice.user_name}</b> has been added. Please note
              down the password:{" "}
              <b className="border bg-white p-1 rounded">
                {userAddedNotice.password}
              </b>
            </div>
          </Alert>
        )}
        {passwordNotice && (
          <Alert color="success" toggle={() => setPasswordNotice(null)} isOpen>
            <div>
              Password reset for <b>{passwordNotice.user_name}</b>. The new
              password is:{" "}
              <b className="border bg-white p-1 rounded">
                {passwordNotice.password}
              </b>
            </div>
          </Alert>
        )}
        <Table bordered responsive size="sm">
          <thead className="table-secondary">
            <tr>
              <th>Name</th>
              <th>Mobile</th>
              <th>Manage Users</th>
              <th>View Price</th>
              <th>Order Updates</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                {editingId === u.id ? (
                  <>
                    <td>
                      <Input
                        bsSize="sm"
                        type="text"
                        name="name"
                        value={editedUser.name}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        type="text"
                        name="mobile"
                        value={editedUser.mobile}
                        onChange={handleEditChange}
                      />
                    </td>
                    {[
                      "manage_users",
                      "view_price",
                      "receive_order_updates",
                    ].map((field) => (
                      <td key={field} className="text-center">
                        <FormGroup switch>
                          <Input
                            type="switch"
                            name={field}
                            checked={!!editedUser[field]}
                            onChange={handleEditChange}
                          />
                        </FormGroup>
                      </td>
                    ))}
                    <td>
                      <Button
                        size="sm"
                        color="primary"
                        className="me-2"
                        onClick={handleEditSave}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        color="outline-secondary"
                        onClick={handleEditCancel}
                      >
                        Cancel
                      </Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{u.name}</td>
                    <td>{u.mobile}</td>
                    <td className="text-center">
                      {u.manage_users ? "Yes" : "No"}
                    </td>
                    <td className="text-center">
                      {u.view_price ? "Yes" : "No"}
                    </td>
                    <td className="text-center">
                      {u.receive_order_updates ? "Yes" : "No"}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => handleEditToggle(u)}
                        id={`edit_user_${u.id}`}
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      <UncontrolledTooltip
                        target={`edit_user_${u.id}`}
                        placement="bottom"
                      >
                        Change name, mobile or access
                      </UncontrolledTooltip>
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => handleResetPassword(u)}
                        id={`reset_password_${u.id}`}
                      >
                        <i className="fa fa-refresh" />
                      </Button>
                      <UncontrolledTooltip
                        target={`reset_password_${u.id}`}
                        placement="bottom"
                      >
                        Reset password
                      </UncontrolledTooltip>
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => alert("Coming soon")}
                        id={`remove_user_${u.id}`}
                        style={{ color: "#CCCCCC" }}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                      <UncontrolledTooltip
                        target={`remove_user_${u.id}`}
                        placement="bottom"
                      >
                        Please contact support for removing users
                      </UncontrolledTooltip>
                    </td>
                  </>
                )}
              </tr>
            ))}

            {addingUser && (
              <tr>
                <td>
                  <Input
                    bsSize="sm"
                    type="text"
                    name="name"
                    value={newUser.name}
                    onChange={handleAddChange}
                  />
                </td>
                <td>
                  <Input
                    bsSize="sm"
                    type="text"
                    name="mobile"
                    value={newUser.mobile}
                    onChange={handleAddChange}
                  />
                </td>
                {["manage_users", "view_price", "receive_order_updates"].map(
                  (field) => (
                    <td key={field} className="text-center">
                      <FormGroup switch>
                        <Input
                          type="switch"
                          name={field}
                          checked={!!newUser[field]}
                          onChange={handleAddChange}
                        />
                      </FormGroup>
                    </td>
                  )
                )}
                <td>
                  <Button
                    size="sm"
                    color="primary"
                    className="me-2"
                    onClick={handleAddUser}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    color="outline-secondary"
                    onClick={() => {
                      setAddingUser(false);
                      setErrors([]);
                    }}
                  >
                    Cancel
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {!addingUser && (
          <Button size="sm" color="link" onClick={() => setAddingUser(true)}>
            <i className="fa fa-plus" /> Add User
          </Button>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UserManagementModal;
